import { useMemo, useState } from "react";
import {
  useDelete,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useUpdate,
  type RaRecord,
} from "react-admin";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import DownloadIcon from "@mui/icons-material/Download";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { formatListDate } from "@/utils/date-format";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import { hasPermission } from "@/utils/permission-utils";
import RazaRequestCreateLagatDialog from "./RazaRequestCreateLagatDialog";
import RazaRequestLinkLagatDialog from "./RazaRequestLinkLagatDialog";

type LagatReceiptRow = {
  id: string;
  receiptNo: string | null;
  amount: number | null;
  receiptDate: string | Date | null;
  paymentMode: string | null;
  paymentRef: string | null;
  razaEventId: string | null;
};

type RazaEventForLagatLabel = {
  id: string;
  date: string | Date;
  startTime?: string | null;
  title: string | null;
};

type RazaLagatRecord = RaRecord & {
  id?: string;
  itsNo?: string | null;
  name?: string | null;
  purpose?: string | null;
  lagatReceipts?: LagatReceiptRow[];
  razaEvents?: RazaEventForLagatLabel[];
};

function formatInr(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "INR" }).format(
    Number(amount)
  );
}

function scopeLabel(
  razaEventId: string | null | undefined,
  eventById: Map<string, RazaEventForLagatLabel>
): string {
  if (!razaEventId) return "General (request)";
  const ev = eventById.get(String(razaEventId));
  if (!ev) return "Event (unknown event)";
  const title = ev.title?.trim() || "Event";
  const datePart = formatListDate(ev.date, { empty: "" });
  const timePart = ev.startTime ? formatRazaEventStartTimeLabel(ev.startTime) : "";
  return [title, datePart, timePart].filter(Boolean).join(" · ");
}

export type RazaRequestLagatSectionProps = {
  /** `general` shows only request-level receipts (no event); `full` lists every receipt on the request. */
  variant?: "full" | "general";
};

const RazaRequestLagatSection = ({ variant = "full" }: RazaRequestLagatSectionProps) => {
  const record = useRecordContext<RazaLagatRecord>();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading: unlinking }] = useUpdate();
  const [deleteReceipt, { isLoading: deleting }] = useDelete();
  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkConfirmLagatId, setUnlinkConfirmLagatId] = useState<string | null>(null);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);

  const canCreate = hasPermission(permissions, "lagatReceipts.create");
  const canView = hasPermission(permissions, "lagatReceipts.view");
  const canEdit = hasPermission(permissions, "lagatReceipts.edit");
  const canDelete = hasPermission(permissions, "lagatReceipts.delete");
  const busy = unlinking || deleting;
  const razaRequestId = record?.id != null ? String(record.id) : "";

  const allReceipts = useMemo(() => record?.lagatReceipts ?? [], [record?.lagatReceipts]);
  const receipts = useMemo(
    () =>
      variant === "general"
        ? allReceipts.filter((r) => r.razaEventId == null || String(r.razaEventId).trim() === "")
        : allReceipts,
    [allReceipts, variant]
  );
  const eventById = useMemo(() => {
    const m = new Map<string, RazaEventForLagatLabel>();
    (record?.razaEvents ?? []).forEach((e) => m.set(String(e.id), e));
    return m;
  }, [record?.razaEvents]);

  const totalAmount = useMemo(
    () => receipts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
    [receipts]
  );

  const unlinkReceiptFromRequest = () => {
    const lagatId = unlinkConfirmLagatId;
    if (!lagatId || !canEdit) return;
    update(
      "lagatReceipts",
      {
        id: lagatId,
        data: { razaRequestId: null },
        previousData: { id: lagatId, razaRequestId } as RaRecord,
      },
      {
        onSuccess: () => {
          setPermanentDeleteDialogOpen(false);
          setUnlinkConfirmLagatId(null);
          notify("Receipt removed from this raza request", { type: "success" });
          refresh();
        },
        onError: (err) => {
          notify(err instanceof Error ? err.message : "Update failed", { type: "error" });
        },
      }
    );
  };

  const deleteReceiptPermanently = () => {
    const lagatId = unlinkConfirmLagatId;
    if (!lagatId || !canDelete) return;
    deleteReceipt(
      "lagatReceipts",
      { id: lagatId, previousData: { id: lagatId } as RaRecord },
      {
        onSuccess: () => {
          setPermanentDeleteDialogOpen(false);
          setUnlinkConfirmLagatId(null);
          notify("Receipt deleted", { type: "success" });
          refresh();
        },
        onError: (err) => {
          setPermanentDeleteDialogOpen(false);
          notify(err instanceof Error ? err.message : "Delete failed", { type: "error" });
        },
      }
    );
  };

  const viewReceipt = (id: string) => {
    window.open(`#/lagat-rcpt/${id}`, "_blank");
  };

  if (!razaRequestId || !record) {
    return (
      <Typography variant="body2" color="text.secondary">
        Save the raza request first to manage lagat.
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      {variant === "general" ? (
        <Alert severity="info">
          <strong>General lagat</strong> is not tied to any event (example: Hajj/Umrah). Use this
          section when a receipt does not belong to a specific event.
        </Alert>
      ) : (
        <Alert severity="info">
          One raza request can have <strong>multiple lagats</strong> (common for weddings across
          multiple days). Link each receipt to its matching event; each event can have one lagat.
        </Alert>
      )}

      {totalAmount > 0 ? (
        <Typography variant="body2" color="text.secondary">
          Total lagat on this request:{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{ fontWeight: 600 }}
            color="text.primary"
          >
            {formatInr(totalAmount)}
          </Typography>
        </Typography>
      ) : null}

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Receipts
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          {canCreate ? (
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              onClick={() => setCreateOpen(true)}
            >
              Create lagat receipt
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              startIcon={<LinkIcon />}
              variant="outlined"
              size="small"
              onClick={() => setLinkOpen(true)}
            >
              Link existing receipt
            </Button>
          ) : null}
        </Stack>
        {!canCreate && !canEdit ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You do not have permission to create or link lagat receipts.
          </Typography>
        ) : null}

        {receipts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No lagat receipts are linked to this request yet.
          </Typography>
        ) : (
          <Paper variant="outlined" sx={{ overflow: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt no.</TableCell>
                  {variant === "full" ? <TableCell>Scope</TableCell> : null}
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Receipt date</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipts.map((lr) => (
                  <TableRow key={lr.id}>
                    <TableCell>{lr.receiptNo?.trim() || lr.id}</TableCell>
                    {variant === "full" ? (
                      <TableCell sx={{ maxWidth: 280 }}>
                        {scopeLabel(lr.razaEventId, eventById)}
                      </TableCell>
                    ) : null}
                    <TableCell align="right">{formatInr(lr.amount)}</TableCell>
                    <TableCell>{formatListDate(lr.receiptDate, { empty: "—" })}</TableCell>
                    <TableCell>
                      {lr.paymentMode?.trim() || "—"}
                      {lr.paymentRef?.trim() ? ` — ${lr.paymentRef.trim()}` : null}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canView ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => viewReceipt(lr.id)}
                            startIcon={<DownloadIcon fontSize="small" />}
                          >
                            View receipt
                          </Button>
                        ) : null}
                        {canEdit ? (
                          <Button
                            component={RouterLink}
                            to={`/lagatReceipts/${lr.id}/edit`}
                            size="small"
                          >
                            Open
                          </Button>
                        ) : null}
                        {canEdit || canDelete ? (
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            onClick={() => {
                              setPermanentDeleteDialogOpen(false);
                              setUnlinkConfirmLagatId(lr.id);
                            }}
                            disabled={busy}
                          >
                            Unlink or delete…
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      <RazaRequestLinkLagatDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        razaRequestId={razaRequestId}
      />
      {createOpen ? (
        <RazaRequestCreateLagatDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          razaRequestId={razaRequestId}
          record={record}
        />
      ) : null}

      <Dialog
        open={unlinkConfirmLagatId != null}
        onClose={() => {
          if (!busy) {
            setPermanentDeleteDialogOpen(false);
            setUnlinkConfirmLagatId(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lagat receipt</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText component="div">
              Remove this receipt from the raza request to keep it in the system (you can link it
              again later), or delete the receipt entirely if you no longer need it.
            </DialogContentText>
            {canEdit ? (
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => unlinkReceiptFromRequest()}
                  disabled={busy}
                  fullWidth
                >
                  {unlinking ? "Updating…" : "Remove from request"}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  Unlinks the receipt from this request; it remains available under lagat receipts.
                </Typography>
              </Stack>
            ) : null}
            {canDelete ? (
              <Stack spacing={1}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setPermanentDeleteDialogOpen(true)}
                  disabled={busy}
                  fullWidth
                >
                  {deleting ? "Deleting…" : "Delete receipt"}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  Soft-deletes the receipt record (same as delete on the lagat list).
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPermanentDeleteDialogOpen(false);
              setUnlinkConfirmLagatId(null);
            }}
            disabled={busy}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={permanentDeleteDialogOpen && unlinkConfirmLagatId != null}
        onClose={() => {
          if (!deleting) setPermanentDeleteDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete receipt permanently</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this lagat receipt permanently? This cannot be undone from here.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={() => deleteReceiptPermanently()}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default RazaRequestLagatSection;
